import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconInfoCircle } from '@tabler/icons-react';
import styles from './InfoHint.module.scss';

interface InfoHintProps {
  /** The explanation shown on hover, focus or tap (and read out by screen readers). */
  text: string;
}

const GAP = 8;
const MARGIN = 12;

/**
 * A small info icon next to a label that explains it. The tooltip shows at
 * once on hover, keyboard focus or tap (touch has no hover) and closes on a
 * tap elsewhere or Escape. It is rendered into the body so a dialog's overflow or a label's styling can
 * never clip or hide it - the browser's own title tooltip was slow to appear
 * and did not show at all in some places.
 */
const InfoHint: React.FC<InfoHintProps> = ({ text }) => {
  const id = useId();
  const iconRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>();

  const place = useCallback(() => {
    const icon = iconRef.current?.getBoundingClientRect();
    const tip = tipRef.current?.getBoundingClientRect();
    if (!icon || !tip) return;
    // above the icon unless there is no room, centred and kept on screen
    const below = icon.top - tip.height - GAP < MARGIN;
    const top = below ? icon.bottom + GAP : icon.top - tip.height - GAP;
    const centred = icon.left + icon.width / 2 - tip.width / 2;
    const left = Math.min(Math.max(centred, MARGIN), window.innerWidth - tip.width - MARGIN);
    setPosition({ top, left });
  }, []);

  useEffect(() => {
    if (!open) {
      setPosition(undefined);
      return;
    }
    place();
    const close = (event: Event) => {
      if (event.type === 'keydown') {
        if ((event as KeyboardEvent).key !== 'Escape') return;
        event.preventDefault(); // Escape closes the tooltip, not the dialog around it
      }
      if (event.type === 'pointerdown' && iconRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close, true);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', close, true);
    };
  }, [open, place]);

  return (
    <>
      <span
        ref={iconRef}
        className={styles.icon}
        role="button"
        tabIndex={0}
        aria-label={text}
        aria-describedby={open ? id : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        // a tap must not also toggle the checkbox or open the select of the label it sits in
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen(true);
        }}
      >
        <IconInfoCircle size={15} stroke={1.8} />
      </span>
      {open &&
        createPortal(
          <div
            ref={tipRef}
            id={id}
            role="tooltip"
            className={styles.tooltip}
            style={position ? { top: position.top, left: position.left } : { visibility: 'hidden', top: 0, left: 0 }}
          >
            {text}
          </div>,
          document.body,
        )}
    </>
  );
};

export default InfoHint;
